import { useContext, lazy } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router';
import AppContainer from './AppContainer';
import ProtectedArea from './pages/ProtectedArea';
import packageJson from './../package.json';
import { ROLE } from './shared/constants';
import { ROUTE_PATTERNS, getDefaultLandingRoute } from './shared/routes';
import { AuthContext } from './context/AuthContext';
import { useAppVersionRefresh } from './hooks/useAppVersionRefresh';
import { withRoleGuard } from './HOC/WithRoleGuard';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';

const Invitation = lazy(() => import('./pages/invitation/Invitation2'));
const SignInWithTenant = lazy(() => import('./pages/SignInWithTenant'));

const TenantsList = lazy(() => import('./pages/root/TenantsList'));
const ClientsList = lazy(() => import('./pages/root/ClientsList'));
const ClientRoot = lazy(() => import('./pages/root/ClientRoot'));

const TherapistsPage = lazy(() => import('./pages/therapists/TherapistsPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const HistoryPage = lazy(() => import('./pages/history/HistoryPage'));
const ClientsInvitations = lazy(() => import('./pages/ClientsInvitations'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const TherapistManagement = lazy(
  () => import('./pages/therapist-management/TherapistManagement')
);

const OpenClient = lazy(() => import('./pages/client/OpenClient'));
const Client = lazy(() => import('./pages/client/Client'));
const ClientProfile = lazy(() => import('./pages/client/ClientProfile'));
const ClientAnalytics = lazy(() => import('./pages/client/ClientAnalytics'));
const ClientJournal = lazy(() => import('./pages/client/ClientJournal'));
const ClientContent = lazy(() => import('./pages/client/ClientContent'));
const ClientPersonalPlan = lazy(
  () => import('./pages/client/ClientPersonalPlan')
);

const App = () => {
  const { role, ownTenantId, isTenantSuperAdminInTherapistMode } =
    useContext(AuthContext)!;

  const effectiveRole =
    role === ROLE.TENANT_SUPER_ADMIN && isTenantSuperAdminInTherapistMode
      ? ROLE.TENANT_ADMIN
      : role;

  useAppVersionRefresh();

  return (
    <HashRouter>
      <Routes>
        <Route element={<AppContainer />}>
          <Route path={ROUTE_PATTERNS.SIGN_IN} element={<SignInWithTenant />} />
          <Route path={ROUTE_PATTERNS.INVITATION} element={<Invitation />} />
          <Route element={<ProtectedArea />}>
            <Route
              index
              path="/"
              element={
                <>
                  {withRoleGuard({
                    role: role,
                    allowedRoles: [ROLE.SUPER_ADMIN],
                    children: <TenantsList />,
                  })}
                  {withRoleGuard({
                    role: role,
                    allowedRoles: [ROLE.TENANT_ADMIN, ROLE.TENANT_SUPER_ADMIN],
                    children: (
                      <Navigate
                        to={getDefaultLandingRoute(
                          role,
                          isTenantSuperAdminInTherapistMode,
                          ownTenantId!
                        )}
                        replace
                      />
                    ),
                  })}
                  {withRoleGuard({
                    role: role,
                    allowedRoles: [ROLE.TENANT_USER],
                    children: <ClientRoot />,
                  })}
                </>
              }
            />

            <Route
              path={ROUTE_PATTERNS.TENANTS_THERAPISTS}
              element={withRoleGuard({
                role: effectiveRole,
                allowedRoles: [ROLE.SUPER_ADMIN, ROLE.TENANT_SUPER_ADMIN],
                redirectPath: '/',
                children: <TherapistsPage />,
              })}
            />
            <Route
              path={ROUTE_PATTERNS.TENANTS_DASHBOARD}
              element={withRoleGuard({
                role: effectiveRole,
                allowedRoles: [ROLE.SUPER_ADMIN, ROLE.TENANT_SUPER_ADMIN],
                redirectPath: '/',
                children: <DashboardPage />,
              })}
            />
            <Route
              path={ROUTE_PATTERNS.TENANTS_HISTORY}
              element={<HistoryPage />}
            />
            <Route
              path={ROUTE_PATTERNS.TENANTS_CLIENTS}
              element={<ClientsList />}
            />

            <Route
              path={ROUTE_PATTERNS.CLIENTS_INVITATIONS}
              element={<ClientsInvitations />}
            />
            <Route path={ROUTE_PATTERNS.SETTINGS} element={<Settings />} />
            <Route
              path={ROUTE_PATTERNS.TENANTS_SETTINGS}
              element={<Settings />}
            />
            <Route
              path={ROUTE_PATTERNS.THERAPIST_MANAGEMENT}
              element={withRoleGuard({
                role: role,
                allowedRoles: [ROLE.TENANT_SUPER_ADMIN],
                redirectPath: '/',
                children: <TherapistManagement />,
              })}
            />
            <Route path={ROUTE_PATTERNS.OPEN_CLIENT} element={<OpenClient />} />
            <Route
              path={ROUTE_PATTERNS.CLIENT}
              element={withRoleGuard({
                role: effectiveRole,
                allowedRoles: [ROLE.TENANT_ADMIN],
                redirectPath: '/',
                children: <Client />,
              })}
            >
              <Route
                path={ROUTE_PATTERNS.CLIENT_PROFILE}
                element={<ClientProfile />}
              />
              <Route
                path={ROUTE_PATTERNS.CLIENT_ANALYTICS}
                element={<ClientAnalytics />}
              />
              <Route
                path={ROUTE_PATTERNS.CLIENT_JOURNAL}
                element={<ClientJournal />}
              />
              <Route
                path={ROUTE_PATTERNS.CLIENT_CONTENT}
                element={<ClientContent />}
              />
              <Route
                path={ROUTE_PATTERNS.CLIENT_PERSONAL_PLAN}
                element={<ClientPersonalPlan />}
              />
            </Route>
          </Route>
        </Route>
      </Routes>
      <div
        style={{
          position: 'fixed',
          bottom: '15px',
          right: '15px',
          fontSize: '0.65rem',
        }}
      >
        v.{packageJson.version}
      </div>
    </HashRouter>
  );
};

export default App;
